/**
 * Local persistence for LUMEN
 * - Full per-trial data in IndexedDB (user owns their cognition)
 * - Quick personal stats computed client-side
 * - Export to CSV
 */

import type { ProbeRecord, ExposureRecord, SARTResult, SceneFamily, RestorationDelta } from './types';

const DB_NAME = 'lumen-clearing';
const DB_VERSION = 1;
const STORE_PROBES = 'probes';
const STORE_EXPOSURES = 'exposures';

let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_PROBES)) {
        const s = db.createObjectStore(STORE_PROBES, { keyPath: 'id', autoIncrement: true });
        s.createIndex('ts', 'ts');
        s.createIndex('phase', 'phase');
      }
      if (!db.objectStoreNames.contains(STORE_EXPOSURES)) {
        const s = db.createObjectStore(STORE_EXPOSURES, { keyPath: 'id', autoIncrement: true });
        s.createIndex('ts', 'ts');
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

export async function saveProbe(p: Omit<ProbeRecord, 'id'>): Promise<number> {
  const db = await openDB();
  return new Promise((res, rej) => {
    const tx = db.transaction(STORE_PROBES, 'readwrite');
    const store = tx.objectStore(STORE_PROBES);
    const rec = { ...p, id: undefined };
    const r = store.add(rec);
    r.onsuccess = () => res(r.result as number);
    r.onerror = () => rej(r.error);
  });
}

export async function saveExposure(e: ExposureRecord): Promise<number> {
  const db = await openDB();
  return new Promise((res, rej) => {
    const tx = db.transaction(STORE_EXPOSURES, 'readwrite');
    const store = tx.objectStore(STORE_EXPOSURES);
    const r = store.add(e);
    r.onsuccess = () => res(r.result as number);
    r.onerror = () => rej(r.error);
  });
}

export async function getAllProbes(): Promise<ProbeRecord[]> {
  const db = await openDB();
  return new Promise((res, rej) => {
    const tx = db.transaction(STORE_PROBES, 'readonly');
    const store = tx.objectStore(STORE_PROBES);
    const r = store.getAll();
    r.onsuccess = () => res(r.result as ProbeRecord[]);
    r.onerror = () => rej(r.error);
  });
}

export async function getAllExposures(): Promise<(ExposureRecord & { id: number })[]> {
  const db = await openDB();
  return new Promise((res, rej) => {
    const tx = db.transaction(STORE_EXPOSURES, 'readonly');
    const store = tx.objectStore(STORE_EXPOSURES);
    const r = store.getAll();
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  });
}

export async function exportAllAsCSV(): Promise<string> {
  const probes = await getAllProbes();
  const exposures = await getAllExposures();

  const lines: string[] = [];
  lines.push('type,ts,phase,scene_family,trials,commission,omission,mean_rt,sd_rt,cv_rt,interrupted,duration_ms,fascination,being_away,extent,compatibility,public_reflection');

  for (const p of probes) {
    lines.push([
      'probe',
      p.ts,
      p.phase,
      p.sceneFamily || '',
      p.result.trials,
      p.result.commissionErrors,
      p.result.omissionErrors,
      p.result.meanRT,
      p.result.sdRT,
      p.result.cvRT,
      p.result.interrupted ? 1 : 0,
      p.result.durationMs,
      '', '', '', '', ''
    ].join(','));
  }

  for (const e of exposures) {
    lines.push([
      'exposure',
      e.ts,
      '',
      e.sceneFamily,
      '', '', '', '', '', '', '',
      e.durationS,
      e.ratings.fascination,
      e.ratings.beingAway,
      e.ratings.extent,
      e.ratings.compatibility,
      (e.publicReflection || '').replace(/,/g, ';')
    ].join(','));
  }

  return lines.join('\n');
}

// Compute personal Lumen stats entirely client-side
export async function computePersonalStats(): Promise<{
  sessions: number;
  avgDeltaCV: number | null;
  bestScene: SceneFamily | null;
  bestDelta: number | null;
  history: Array<{ ts: number; scene: SceneFamily; deltaCV: number; lumen: number }>;
}> {
  const probes = await getAllProbes();
  const exposures = await getAllExposures();

  // Pair pre/post by rough time proximity + scene
  const history: Array<{ ts: number; scene: SceneFamily; deltaCV: number; lumen: number }> = [];
  let bestScene: SceneFamily | null = null;
  let bestDelta = Infinity;

  // Simple pairing: for each post, find closest previous pre within last 30min
  const preProbes = probes.filter(p => p.phase === 'pre');
  const postProbes = probes.filter(p => p.phase === 'post');

  let totalDeltaCV = 0;
  let paired = 0;

  for (const post of postProbes) {
    if (!post.sceneFamily) continue;

    // find matching pre
    const candidates = preProbes.filter(pre =>
      pre.sceneFamily === post.sceneFamily &&
      Math.abs(pre.ts - post.ts) < 1000 * 60 * 35 &&
      pre.ts < post.ts
    );
    if (candidates.length === 0) continue;

    const pre = candidates.sort((a, b) => b.ts - a.ts)[0]; // most recent before

    const deltaCV = post.result.cvRT - pre.result.cvRT; // negative = improvement
    const deltaComm = post.result.commissionErrors - pre.result.commissionErrors;

    // Lumen composite: reward reduction in both variability and commissions
    // Scale to roughly 0-100 "restoration units"
    const lumen = Math.max(0, Math.round(
      (-deltaCV * 420) + (-deltaComm * 5.5) + 12
    ));

    history.push({
      ts: post.ts,
      scene: post.sceneFamily,
      deltaCV,
      lumen,
    });

    totalDeltaCV += deltaCV;
    paired++;

    if (deltaCV < bestDelta) {
      bestDelta = deltaCV;
      bestScene = post.sceneFamily;
    }
  }

  const avgDeltaCV = paired > 0 ? totalDeltaCV / paired : null;

  return {
    sessions: paired,
    avgDeltaCV,
    bestScene,
    bestDelta: bestDelta === Infinity ? null : bestDelta,
    history: history.sort((a, b) => a.ts - b.ts),
  };
}

export async function clearAllData(): Promise<void> {
  const db = await openDB();
  const tx1 = db.transaction(STORE_PROBES, 'readwrite');
  tx1.objectStore(STORE_PROBES).clear();
  const tx2 = db.transaction(STORE_EXPOSURES, 'readwrite');
  tx2.objectStore(STORE_EXPOSURES).clear();
  return new Promise((res) => setTimeout(res, 30));
}
